import React, { useEffect, useState } from 'react';
import { Check, X } from 'react-feather';
import classnames from 'classnames';
import { Button } from 'components/quashed';
import RouterPromptWrapper from 'components/router-prompt/RouterPromptWrapper';
import Radio from 'components/form-fields-v3/Radio';
import { H1 } from 'components/typography';
import { ChecklistIcon } from 'components/svgs/CheckListIcon';
import { PhoneIcon } from 'components/svgs/PhoneIcon';
import {
  isBreakpoint,
  trackMarketScanStepperClick,
  trackMarketScanStepperPurchaseConfirmation,
  trackMarketScanStepperShown,
} from 'helpers';
import { IPolicyScan } from 'types';
import { StorageKeys } from 'helpers/storage';
import popupStyles from 'components/market-scan-survey-form/marketScanExitPopup.module.scss';
import routerPromptStyles from 'components/router-prompt/routerPromptStyles.module.scss';
import surveyStyles from 'components/market-scan-survey-form/surveyForm.module.scss';
import styles from './MarketScanStepper.module.scss';

interface IProps {
  policyScan?: IPolicyScan;
}

const STEPS = [
  'Your details',
  'Compare options',
  'Select provider',
  'Purchase policy',
  'Cancel old policy',
];
const COMPARE_STEP = 1;
const PURCHASE_STEP = 3;
const CANCEL_STEP = 4;

const PURCHASE_OPTIONS = [
  { key: 'yes', value: 'yes', text: 'Yes, I did' },
  { key: 'no', value: 'no', text: 'No, I did not' },
];

const MarketScanStepper = ({ policyScan }: IProps) => {
  const purchasedStorageKey = `${StorageKeys.MS_STEPPER_PURCHASED}_${policyScan?.id}`;
  const [purchased, setPurchased] = useState(
    () => localStorage.getItem(purchasedStorageKey) === 'true',
  );
  const [openStep, setOpenStep] = useState<number | null>(null);
  const [purchaseAnswer, setPurchaseAnswer] = useState<string | null>(null);

  const hasProvider = !!policyScan?.selectedProvider;

  useEffect(() => {
    if (policyScan?.id && !isBreakpoint('md')) {
      trackMarketScanStepperShown(policyScan);
    }
  }, [policyScan?.id]);

  // Steps 1–3 complete together once a provider is chosen (or a purchase is
  // confirmed, which implies one); step 5 is never completed.
  const isCompleted = (idx: number) => {
    if (idx < COMPARE_STEP) return true;
    if (idx < PURCHASE_STEP) return hasProvider || purchased;
    return idx === PURCHASE_STEP && purchased;
  };

  const currentStep = purchased
    ? CANCEL_STEP
    : hasProvider
      ? PURCHASE_STEP
      : COMPARE_STEP;

  const isClickable = (idx: number) =>
    idx === PURCHASE_STEP || idx === CANCEL_STEP;

  const closePopup = () => setOpenStep(null);

  const handleStepClick = (idx: number) => {
    trackMarketScanStepperClick(STEPS[idx], policyScan);
    if (isClickable(idx)) {
      setPurchaseAnswer(null);
      setOpenStep(idx);
    }
  };

  const handlePurchaseSubmit = () => {
    const confirmed = purchaseAnswer === 'yes';
    if (confirmed) {
      trackMarketScanStepperPurchaseConfirmation(policyScan);
      localStorage.setItem(purchasedStorageKey, 'true');
    } else {
      localStorage.removeItem(purchasedStorageKey);
    }
    setPurchased(confirmed);
    closePopup();
  };

  // Same shell and styles as the purchase exit survey popup.
  const renderPopup = (
    icon: React.ReactNode,
    title: string,
    content: React.ReactNode,
  ) => (
    <RouterPromptWrapper closeModal={closePopup}>
      <div
        className={classnames(
          routerPromptStyles.modalContainer,
          surveyStyles.modal,
        )}
      >
        <div className={surveyStyles.surveyFormContainer}>
          <X className={surveyStyles.close} onClick={closePopup} />
          <div
            className={classnames(surveyStyles.formEngine, styles.popupContent)}
          >
            <div className={styles.popupHeader}>
              {icon}
              <div>
                <H1>{title}</H1>
              </div>
            </div>
            {content}
          </div>
        </div>
      </div>
    </RouterPromptWrapper>
  );

  return (
    <>
      <ul className={styles.steps}>
        {STEPS.map((step, idx) => {
          const completed = isCompleted(idx);
          return (
            <li
              key={`step_${idx}`}
              className={classnames(
                styles.step,
                completed && styles.completed,
                idx === currentStep && styles.current,
                isClickable(idx) && styles.clickable,
              )}
              onClick={() => handleStepClick(idx)}
            >
              <div className={styles.circle}>
                {completed ? (
                  <Check size={14} strokeWidth={4} strokeLinecap="square" />
                ) : (
                  idx + 1
                )}
              </div>
              <div className={styles.label}>{step}</div>
              {idx < STEPS.length - 1 && (
                <div
                  className={classnames(
                    styles.tail,
                    completed && styles.active,
                  )}
                />
              )}
            </li>
          );
        })}
      </ul>

      {openStep === PURCHASE_STEP &&
        renderPopup(
          <ChecklistIcon className={popupStyles.savingsSearchIcon} />,
          'Did you purchase a policy?',
          <>
            <Radio options={PURCHASE_OPTIONS} onChange={setPurchaseAnswer} />
            <Button
              className={styles.popupButton}
              type="button"
              text="Submit"
              disabled={purchaseAnswer === null}
              onClick={handlePurchaseSubmit}
            />
          </>,
        )}

      {openStep === CANCEL_STEP &&
        renderPopup(
          <PhoneIcon className={popupStyles.savingsSearchIcon} />,
          'Cancel your old policy',
          <>
            <p className={popupStyles.cardText}>
              Call or email your previous provider to cancel your old policy.
            </p>
            <p className={popupStyles.cardText}>
              It is best to purchase your new policy before cancelling your old
              policy to ensure continuous protection.
            </p>
            <Button
              className={styles.popupButton}
              type="button"
              text="Got it"
              onClick={closePopup}
            />
          </>,
        )}
    </>
  );
};

export default MarketScanStepper;
